import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { useToast } from '../shell/toast';
import { EMPTY_OTP, isComplete, otpCode, otpReducer } from './otp';
import type { OtpAction, OtpState } from './otp';
import { MESSAGES, emailFormSchema, signInError } from './sign-in';
import type { EmailForm } from './sign-in';
import { useCountdown } from './use-countdown';
import { ME_KEY } from './use-me';
import { useAttempt } from './use-attempt';
import { useCaptcha, withCaptcha } from './use-captcha';

export type SignInStep = 'email' | 'code';

export interface SignIn {
  readonly step: SignInStep;
  readonly email: string;
  readonly otp: OtpState;
  readonly error: string;
  readonly busy: boolean;
  readonly resendIn: number;
  /** The email box, registered with the step's form. */
  readonly emailField: UseFormRegisterReturn<'email'>;
  /** Containers for the bot check's widgets, one per step's action. */
  readonly captchaRefs: {
    readonly code: (element: HTMLDivElement | null) => void;
    readonly verify: (element: HTMLDivElement | null) => void;
  };
  readonly onOtp: (action: OtpAction) => void;
  readonly submit: () => void;
  readonly resend: () => void;
  readonly back: () => void;
}

/**
 * Email → code → session (docs/web-auth.md). On success the user goes straight
 * into the `me` query, which is what takes the overlay away.
 */
export function useSignIn(): SignIn {
  const [step, setStep] = useState<SignInStep>('email');
  const emailForm = useForm<EmailForm>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: { email: '' },
  });
  const email = emailForm.watch('email').trim();
  const [otp, setOtp] = useState<OtpState>(EMPTY_OTP);
  const { error, setError, busy, attempt } = useAttempt();
  const [resendIn, startResend] = useCountdown();
  const queryClient = useQueryClient();
  const toast = useToast();
  // Two widgets: the code step can both resend (action `code`) and verify.
  const codeCaptcha = useCaptcha('code');
  const verifyCaptcha = useCaptcha('verify');

  const sendCode = (address: string): Promise<boolean> =>
    attempt(() =>
      withCaptcha(codeCaptcha, async (captchaToken) => {
        const result = await api.auth.code({ body: { email: address, captchaToken } });
        if (result.status !== 202) return signInError('email', result.status, result.body);
        setStep('code');
        setOtp(EMPTY_OTP);
        startResend(result.body.resendAfterSeconds);
        return undefined;
      }),
    );

  /** The email step's submit: the schema first, in the design's words, then the API. */
  const submitEmail = emailForm.handleSubmit(
    async ({ email: address }) => {
      await sendCode(address);
    },
    (errors) => setError(errors.email?.message ?? MESSAGES.badEmail),
  );

  const verify = async (state: OtpState): Promise<void> => {
    if (!isComplete(state)) {
      setError(MESSAGES.incomplete);
      return;
    }
    await attempt(() =>
      withCaptcha(verifyCaptcha, async (captchaToken) => {
        const code = otpCode(state);
        const result = await api.auth.verify({ body: { email, code, captchaToken } });
        if (result.status !== 200) return signInError('code', result.status, result.body);
        queryClient.setQueryData(ME_KEY, result.body.user);
        toast(`Signed in as ${email}`);
        return undefined;
      }),
    );
  };

  return {
    step,
    email,
    otp,
    error,
    busy,
    resendIn,
    emailField: emailForm.register('email', { onChange: () => setError('') }),
    captchaRefs: { code: codeCaptcha.containerRef, verify: verifyCaptcha.containerRef },
    onOtp: (action) => {
      const next = otpReducer(otp, action);
      setOtp(next);
      if (action.type === 'input') setError('');
      // The sixth digit submits: nobody should have to reach for the button.
      if (isComplete(next) && !isComplete(otp) && !busy) void verify(next);
    },
    submit: () => {
      if (busy) return;
      if (step === 'email') void submitEmail();
      else void verify(otp);
    },
    resend: () => {
      if (resendIn > 0 || busy) return;
      void sendCode(email).then((sent) => sent && toast('New code sent'));
    },
    back: () => {
      setStep('email');
      setError('');
      setOtp(EMPTY_OTP);
    },
  };
}
